import { apiClient } from '@applyonline/shared'
import { MutationFunction, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

const SCOPE = 'identity-verification-session'
const DETAIL = 'detail'

const keysFactory = {
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
}

export interface IdentityVerificationSession {
  id: string
  token: string
}

const getIdentityVerificationSession = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['detail']>>) => {
  try {
    const response = await apiClient.get<IdentityVerificationSession>(
      `/b2c/creditApplication/${encodeURIComponent(id)}/IdentityVerificationSession`,
    )
    return response.data
  } catch {
    return null
  }
}

export function useGetIdentityVerificationSession(
  creditApplicationId: string,
): [IdentityVerificationSession | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.detail(creditApplicationId),
    queryFn: getIdentityVerificationSession,
    enabled: !!creditApplicationId,
  })

  return [data ?? null, isFetching]
}

export function useResetIdentityVerificationSession(creditApplicationId: string) {
  const queryClient = useQueryClient()

  return useCallback(
    () => queryClient.setQueryData(keysFactory.detail(creditApplicationId), null),
    [creditApplicationId, queryClient],
  )
}

const updateIdentityVerificationSession: MutationFunction<
  IdentityVerificationSession,
  IdentityVerificationSession
> = async ({ id, token: sessionToken }: IdentityVerificationSession) => {
  const response = await apiClient.put<IdentityVerificationSession>(
    `/b2c/creditapplication/${encodeURIComponent(id)}/IdentityVerificationSession`,
    {
      token: sessionToken,
    },
  )
  return response.data
}

export function useUpdateIdentityVerificationSession(): [
  MutationFunction<IdentityVerificationSession, IdentityVerificationSession>,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: updateIdentityVerificationSession,
    onSuccess(_data, variables) {
      const creditApplicationId = variables.id
      if (creditApplicationId) {
        queryClient.setQueryData(keysFactory.detail(creditApplicationId), {
          id: creditApplicationId,
          token: variables.token,
        })
      }
    },
  })

  return [mutateAsync, isPending, reset]
}
